import React, { useEffect, useState, useRef } from "react";
import PropTypes from "prop-types";
import DOMPurify from "dompurify";
/*
const baseConfig = {
  tex: {
    inlineMath: [
      ["$", "$"],
      ["\\(", "\\)"],
    ],
    autoload: {
      color: [],
      colorV2: ['color']
    },
    packages: {'[+]': ['noerrors']}
  },
  startup: {
    typeset: false
  },
  options: {
    ignoreHtmlClass: 'tex2jax_ignore',
    processHtmlClass: 'tex2jax_process'
  },
  loader: {
    load: ['input/asciimath', '[tex]/noerrors']
  }
};
*/
const MathJaxPreview = React.forwardRef(({
  script,
  config,
  className,
  math,
  id,
  textID,
  style,
  msDelayDisplay, //milliseconds to delay display of div, 300 is default
  onDisplay,
  onLoad,
  onError,
}, ref) => {
  // DOMPurify.setConfig({ ADD_ATTR: ['target'] }); // This Add Target attribute
  DOMPurify.setConfig({
    ADD_TAGS: ['iframe'],
    ADD_ATTR: ['allow', 'allowfullscreen', 'frameborder', 'scrolling', 'src', 'width', 'height', 'target'],
    ALLOWED_URI_REGEXP: /^https:\/\/(www\.youtube\.com)/,
  });
  const previewRef = useRef();
  const sanitizedMath = DOMPurify.sanitize(math);
  const [display, setDisplay] = useState("none"); //prevent display during processing
  const [loadingState, setLoadingState] = useState(
    window.MathJax ? "loaded" : "loading"
  );

  useEffect(() => {
    let mathjaxScriptTag = document.querySelector(`script[src="${script}"]`);
    if (!mathjaxScriptTag) {
      mathjaxScriptTag = document.createElement("script");
      mathjaxScriptTag.async = true;
      mathjaxScriptTag.src = script;

      for (const [k, v] of Object.entries(config || {})) {
        mathjaxScriptTag.setAttribute(k, v);
      }
      const node = document.head || document.getElementsByTagName("head")[0];
      node.appendChild(mathjaxScriptTag);
    }
    const onloadHandler = () => {
      setLoadingState("loaded");
      //window.MathJax.Hub.Config({ ...baseConfig, ...config });
      onLoad && onLoad();
    };
    const onerrorHandler = () => {
      setLoadingState("failed");
      onError && onError();
    };

    mathjaxScriptTag.addEventListener("load", onloadHandler);
    mathjaxScriptTag.addEventListener("error", onerrorHandler);

    return () => {
      mathjaxScriptTag.removeEventListener("load", onloadHandler);
      mathjaxScriptTag.removeEventListener("error", onloadHandler);
    };
  }, [setLoadingState, config,onError,onLoad,script]);

  useEffect(() => {
    if (loadingState !== "loaded") {
      return;
    }
    window.MathJax.texReset();
    /* Since the "a" tag is placed as string, we are replacing the entity tags (&lt, &gt) with < and > to be shown as link in the preview
      Screenshots:
      Before replacing - https://loom.com/i/adb5498cda5541488ed001b5a01e05bb
      After replacing -  https://loom.com/i/87a9df3b78c244e7841ca64c4bd72f11
    */
      previewRef.current.innerHTML = sanitizedMath.replace(/&lt;a href/g,'<a href').replace(/&lt;\/a&gt;/g,'</a>').replace(/&gt;/g,'>');
    // window.MathJax.typeset();
    window.MathJax.typesetPromise();
   /* window.MathJax.Hub.Queue([
      "Typeset",
      window.MathJax.Hub,
      previewRef.current,
    ]);*/

    //delay display of div
    var msDelay;
    if ( //msDelayDisplay prop is a reasonable number of ms
      msDelayDisplay !== null &&
      !isNaN(+msDelayDisplay) &&
      +msDelayDisplay >= 0 &&
      +msDelayDisplay < 10000
    ) {
      msDelay = +msDelayDisplay;
    } else {
      msDelay = 300; // default 300ms delay
    }
    const timeout = setTimeout(() => {
      setDisplay("inline"); //display div after delay, hopefully typeset has finished
      onDisplay && onDisplay();
    }, msDelay);

    return () => {
      setDisplay("none");
      clearTimeout(timeout);
    };
  }, [sanitizedMath, loadingState, previewRef,msDelayDisplay,onDisplay]);
  return (
    <div className={className} id={id} style={{ display: display, ...style }} ref={ref}>
      {loadingState === "failed" && <span>fail loading mathjax lib</span>}
      <div className="react-mathjax-preview-result tex2jax_process" id={textID} ref={previewRef} />
    </div>
  );
});

MathJaxPreview.displayName = 'MathJaxPreview';

MathJaxPreview.propTypes = {
  script: PropTypes.string,
  config: PropTypes.object,
  className: PropTypes.string,
  math: PropTypes.string,
  style: PropTypes.object,
  id: PropTypes.string,
  onLoad: PropTypes.func,
  onError: PropTypes.func,
  onDisplay: PropTypes.func,
};

MathJaxPreview.defaultProps = {
  script:
    "https://cdn.jsdelivr.net/npm/mathjax@3/es5/tex-svg.js",
  id: "react-mathjax-preview",
};

export default MathJaxPreview;
